/**
 * Testo delle risposte di "Chiedi all'AI": grassetti **x**, titoli ##, elenchi.
 * Spostato da UnifiedRightPanel per essere usato anche nel pannello Instagram/Messenger.
 */
import React from 'react';

export function AiMessageContent({ content, isUser }: { content: string; isUser: boolean }) {
  if (isUser) {
    return <p className="text-[11px] leading-[1.6] whitespace-pre-wrap">{content}</p>;
  }

  // Parse markdown-like formatting for AI responses
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let listKey = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`list-${listKey++}`} className="space-y-0.5 my-1">{listItems}</ul>);
      listItems = [];
    }
  };

  const formatInline = (text: string): React.ReactNode[] => {
    // Bold **text** and bullet symbols
    const parts: React.ReactNode[] = [];
    const regex = /\*\*(.+?)\*\*/g;
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      parts.push(<strong key={`b-${key++}`} className="font-semibold">{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts;
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    // Empty line
    if (!trimmed) {
      flushList();
      return;
    }

    // Headers: ## or ###
    if (trimmed.startsWith('## ') || trimmed.startsWith('### ')) {
      flushList();
      const headerText = trimmed.replace(/^#{2,3}\s+/, '');
      elements.push(
        <p key={`h-${i}`} className="text-[11px] font-bold text-[#37352F] dark:text-white mt-1.5 mb-0.5">
          {formatInline(headerText)}
        </p>
      );
      return;
    }

    // Bullet points: - • ▸ ✓ ✅ or numbered 1. 2.
    const bulletMatch = trimmed.match(/^(?:[-•▸✓✅]\s*|(?:\d+\.)\s+)(.*)$/);
    if (bulletMatch) {
      const symbol = trimmed.startsWith('✓') || trimmed.startsWith('✅') ? '✓ ' : '• ';
      listItems.push(
        <li key={`li-${i}`} className="text-[11px] leading-[1.6] text-[#37352F] dark:text-white/90 flex gap-1">
          <span className="text-purple-500 flex-shrink-0">{symbol}</span>
          <span>{formatInline(bulletMatch[1])}</span>
        </li>
      );
      return;
    }

    // Regular paragraph
    flushList();
    elements.push(
      <p key={`p-${i}`} className="text-[11px] leading-[1.6] text-[#37352F] dark:text-white/90">
        {formatInline(trimmed)}
      </p>
    );
  });

  flushList();

  return <div className="space-y-0.5">{elements}</div>;
}
